// src/components/super-admin/SuperAdminPanel.tsx
import { useState, useEffect } from 'react'
import { Routes, Route, Link, useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Organization } from '../../types'
import { Card, CardHeader, CardBody, Button, Input, Badge, Logo } from '../ui'

export function SuperAdminPanel() {
  const location = useLocation()

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Super Admin Header */}
      <header className="dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Logo className="h-4" />
            <div className="text-sm text-gray-400">|</div>
            <div className="text-sm font-medium text-gray-900">Super Admin</div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Super Admin Header */}
        <div className="py-6">
          <h1 className="text-3xl font-bold text-gray-900">
            ScriptYak Super Admin
          </h1>
          <p className="mt-2 text-gray-600">
            Manage organizations, users, and platform settings
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            <Link
              to="/super-admin"
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                location.pathname === '/super-admin'
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Dashboard
            </Link>
            <Link
              to="/super-admin/organizations"
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                location.pathname === '/super-admin/organizations'
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Organizations
            </Link>
            <Link
              to="/super-admin/users"
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                location.pathname === '/super-admin/users'
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All Users
            </Link>
            <Link
              to="/super-admin/analytics"
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                location.pathname === '/super-admin/analytics'
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Analytics
            </Link>
          </nav>
        </div>

        {/* Super Admin Content */}
        <Routes>
          <Route index element={<SuperAdminDashboard />} />
          <Route path="organizations" element={<OrganizationManagement />} />
          <Route path="users" element={<AllUsersManagement />} />
          <Route path="analytics" element={<PlatformAnalytics />} />
        </Routes>
      </div>
    </div>
  )
}

// Super Admin Dashboard with updated styling
function SuperAdminDashboard() {
  const [stats, setStats] = useState({
    totalOrganizations: 0,
    totalUsers: 0,
    activeOrganizations: 0,
    totalTickets: 0,
  })

  useEffect(() => {
    async function loadStats() {
      // Get organization count
      const { count: orgCount } = await supabase
        .from('organizations')
        .select('*', { count: 'exact' })

      // Get active organization count
      const { count: activeOrgCount } = await supabase
        .from('organizations')
        .select('*', { count: 'exact' })
        .eq('is_active', true)

      // Get user count
      const { count: userCount } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact' })

      // Get ticket count
      const { count: ticketCount } = await supabase
        .from('tickets')
        .select('*', { count: 'exact' })

      setStats({
        totalOrganizations: orgCount || 0,
        totalUsers: userCount || 0,
        activeOrganizations: activeOrgCount || 0,
        totalTickets: ticketCount || 0,
      })
    }

    loadStats()
  }, [])

  return (
    <div className="space-y-6">
      {/* Platform Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardBody>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">O</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <div className="text-sm font-medium text-gray-500">
                  Total Organizations
                </div>
                <div className="text-lg font-medium text-gray-900">
                  {stats.totalOrganizations}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">A</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <div className="text-sm font-medium text-gray-500">
                  Active Organizations
                </div>
                <div className="text-lg font-medium text-gray-900">
                  {stats.activeOrganizations}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">U</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <div className="text-sm font-medium text-gray-500">
                  Total Users
                </div>
                <div className="text-lg font-medium text-gray-900">
                  {stats.totalUsers}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">T</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <div className="text-sm font-medium text-gray-500">
                  Total Tickets
                </div>
                <div className="text-lg font-medium text-gray-900">
                  {stats.totalTickets}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Quick Actions
          </h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/super-admin/organizations">
              <Button variant="primary" className="w-full">
                Create Organization
              </Button>
            </Link>
            <Link to="/super-admin/users">
              <Button variant="secondary" className="w-full">
                Manage Users
              </Button>
            </Link>
            <Link to="/super-admin/analytics">
              <Button variant="outline" className="w-full">
                View Analytics
              </Button>
            </Link>
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

// Organization Management Component with updated styling
function OrganizationManagement() {
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false)
  const [newOrg, setNewOrg] = useState({
    name: '',
    slug: '',
    domain: '',
    subdomain: '',
  })
  const [loading, setLoading] = useState<boolean>(false)

  const loadOrganizations = async () => {
    const { data } = await supabase
      .from('organizations')
      .select('*')
      .order('created_at', { ascending: false })

    setOrganizations((data as Organization[]) || [])
  }

  useEffect(() => {
    loadOrganizations()
  }, [])

  const createOrganization = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const { error } = await supabase.from('organizations').insert({
        name: newOrg.name,
        slug: newOrg.slug,
        domain: newOrg.domain || null,
        subdomain: newOrg.subdomain,
        is_active: true,
      })

      if (error) throw error

      setNewOrg({ name: '', slug: '', domain: '', subdomain: '' })
      setShowCreateForm(false)
      await loadOrganizations()
    } catch (error) {
      alert(
        `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    } finally {
      setLoading(false)
    }
  }

  const toggleOrganizationStatus = async (
    orgId: string,
    currentStatus: boolean
  ) => {
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ is_active: !currentStatus })
        .eq('id', orgId)

      if (error) throw error
      await loadOrganizations()
    } catch (error) {
      alert(
        `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Organization Management</h2>
        <Button onClick={() => setShowCreateForm(true)}>
          Create Organization
        </Button>
      </div>

      {/* Create Organization Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Create New Organization</h3>
          </CardHeader>
          <CardBody>
            <form onSubmit={createOrganization} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="text"
                  placeholder="Organization Name"
                  value={newOrg.name}
                  onChange={e => setNewOrg({ ...newOrg, name: e.target.value })}
                  required
                />
                <Input
                  type="text"
                  placeholder="Slug (e.g., acme-corp)"
                  value={newOrg.slug}
                  onChange={e => setNewOrg({ ...newOrg, slug: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="text"
                  placeholder="Email Domain (e.g., acme.com)"
                  value={newOrg.domain}
                  onChange={e =>
                    setNewOrg({ ...newOrg, domain: e.target.value })
                  }
                />
                <Input
                  type="text"
                  placeholder="Subdomain (e.g., acme-corp)"
                  value={newOrg.subdomain}
                  onChange={e =>
                    setNewOrg({ ...newOrg, subdomain: e.target.value })
                  }
                  required
                />
              </div>
              <div className="flex space-x-4">
                <Button type="submit" loading={loading}>
                  {loading ? 'Creating...' : 'Create Organization'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {/* Organizations List */}
      <Card>
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Organization</th>
                <th className="table-header-cell">Domain</th>
                <th className="table-header-cell">Subdomain</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {organizations.map(org => (
                <tr key={org.id} className="table-row">
                  <td className="table-cell">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {org.name}
                      </div>
                      <div className="text-sm text-gray-500">{org.slug}</div>
                    </div>
                  </td>
                  <td className="table-cell text-gray-500">
                    {org.domain || 'None'}
                  </td>
                  <td className="table-cell text-gray-500">{org.subdomain}</td>
                  <td className="table-cell">
                    <Badge variant={org.is_active ? 'green' : 'red'}>
                      {org.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="table-cell">
                    <button
                      onClick={() =>
                        toggleOrganizationStatus(org.id, org.is_active)
                      }
                      className={`text-sm font-medium transition-colors ${
                        org.is_active
                          ? 'text-red-600 hover:text-red-900'
                          : 'text-green-600 hover:text-green-900'
                      }`}
                    >
                      {org.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

// Placeholder components with updated styling
function AllUsersManagement() {
  return (
    <Card>
      <CardHeader>
        <h2 className="text-xl font-bold">All Users Management</h2>
      </CardHeader>
      <CardBody>
        <p className="text-gray-600">
          This section will show all users across all organizations with
          filtering and management capabilities.
        </p>
      </CardBody>
    </Card>
  )
}

function PlatformAnalytics() {
  return (
    <Card>
      <CardHeader>
        <h2 className="text-xl font-bold">Platform Analytics</h2>
      </CardHeader>
      <CardBody>
        <p className="text-gray-600">
          This section will show platform-wide analytics and usage metrics.
        </p>
      </CardBody>
    </Card>
  )
}
